import React, { useState } from 'react';
import axios from 'axios';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
};

const S = (isMobile) => ({
  overlay: {
    position: 'fixed', inset: 0, background: '#0a0a0a', zIndex: 2000,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'flex-start', overflowY: 'auto',
    padding: isMobile ? '100px 20px 60px' : '100px 24px 60px',
  },
  progressBar: (pct) => ({
    position: 'fixed', top: 0, left: 0, height: '2px',
    background: '#c9a96e', width: `${pct}%`,
    transition: 'width 0.5s ease', zIndex: 2001,
  }),
  logo: {
    position: 'fixed', top: isMobile ? '20px' : '32px',
    left: isMobile ? '20px' : '48px',
    fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem',
    fontWeight: 300, color: '#c9a96e', letterSpacing: '0.05em', zIndex: 2001,
  },
  stepCount: {
    position: 'fixed', top: isMobile ? '24px' : '36px',
    right: isMobile ? '20px' : '48px',
    fontFamily: 'Jost, sans-serif', fontSize: '0.6rem',
    letterSpacing: '0.3em', color: '#3a3a3a', zIndex: 2001,
  },
  card: { width: '100%', maxWidth: '580px', textAlign: 'center' },
  eyebrow: {
    fontFamily: 'Jost, sans-serif', fontSize: '0.6rem',
    letterSpacing: '0.5em', textTransform: 'uppercase',
    color: '#c9a96e', marginBottom: '20px',
  },
  title: {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: isMobile ? '2.2rem' : 'clamp(2.2rem, 5vw, 3.8rem)',
    fontWeight: 300, color: '#f5f0eb', lineHeight: 1.1, marginBottom: '16px',
  },
  subtitle: {
    fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', fontWeight: 300,
    color: '#665e58', lineHeight: 1.8,
    maxWidth: '440px', margin: '0 auto 32px',
  },
  expGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
    gap: '1px', background: '#2a2a2a', border: '1px solid #2a2a2a',
    marginBottom: '32px',
  },
  expCard: (selected) => ({
    background: selected ? 'rgba(201,169,110,0.06)' : '#111111',
    padding: isMobile ? '24px 20px' : '28px 24px',
    cursor: 'pointer', textAlign: 'left',
    borderBottom: selected ? '2px solid #c9a96e' : '2px solid transparent',
    transition: 'all 0.2s ease',
  }),
  expIcon: {
    fontFamily: 'Cormorant Garamond, serif', fontSize: '1.6rem',
    color: '#c9a96e', fontStyle: 'italic', marginBottom: '10px',
  },
  expTitle: (selected) => ({
    fontFamily: 'Jost, sans-serif', fontSize: '0.7rem',
    letterSpacing: '0.25em', textTransform: 'uppercase',
    color: selected ? '#c9a96e' : '#f5f0eb', marginBottom: '6px',
  }),
  expDesc: {
    fontFamily: 'Jost, sans-serif', fontSize: '0.72rem',
    fontWeight: 300, color: '#4a4a4a', lineHeight: 1.6,
  },
  moodGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
    gap: '1px', background: '#2a2a2a', border: '1px solid #2a2a2a',
    marginBottom: '32px',
  },
  moodCard: (selected) => ({
    background: selected ? 'rgba(201,169,110,0.06)' : '#111111',
    padding: isMobile ? '24px 20px' : '28px 24px',
    cursor: 'pointer', textAlign: 'left',
    borderBottom: selected ? '2px solid #c9a96e' : '2px solid transparent',
    transition: 'all 0.2s ease',
  }),
  moodIcon: {
    fontFamily: 'Cormorant Garamond, serif', fontSize: '1.6rem',
    color: '#c9a96e', fontStyle: 'italic', marginBottom: '10px',
  },
  moodTitle: (selected) => ({
    fontFamily: 'Jost, sans-serif', fontSize: '0.7rem',
    letterSpacing: '0.25em', textTransform: 'uppercase',
    color: selected ? '#c9a96e' : '#f5f0eb', marginBottom: '6px',
  }),
  moodDesc: {
    fontFamily: 'Jost, sans-serif', fontSize: '0.72rem',
    fontWeight: 300, color: '#4a4a4a', lineHeight: 1.6,
  },
  tourGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
    gap: '1px', background: '#2a2a2a', border: '1px solid #2a2a2a',
    marginBottom: '32px', textAlign: 'left',
  },
  tourCard: { background: '#111111', padding: isMobile ? '24px 20px' : '28px 24px' },
  tourIcon: {
    fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem',
    color: '#c9a96e', fontStyle: 'italic', marginBottom: '10px',
  },
  tourTitle: {
    fontFamily: 'Jost, sans-serif', fontSize: '0.62rem',
    letterSpacing: '0.25em', textTransform: 'uppercase',
    color: '#f5f0eb', marginBottom: '8px',
  },
  tourDesc: {
    fontFamily: 'Jost, sans-serif', fontSize: '0.75rem',
    fontWeight: 300, color: '#4a4a4a', lineHeight: 1.6,
  },
  hintText: {
    fontFamily: 'Jost, sans-serif', fontSize: '0.72rem',
    fontWeight: 300, color: '#3a3a3a', marginBottom: '32px',
    lineHeight: 1.7, textAlign: 'center',
  },
  btnRow: {
    display: 'flex', gap: '8px', justifyContent: 'center',
    flexDirection: isMobile ? 'column' : 'row', marginTop: '16px',
  },
  primaryBtn: {
    fontFamily: 'Jost, sans-serif', fontSize: '0.72rem',
    letterSpacing: '0.2em', textTransform: 'uppercase',
    color: '#0a0a0a', background: '#c9a96e', border: 'none',
    padding: '15px 48px', cursor: 'pointer',
    width: isMobile ? '100%' : 'auto',
  },
  skipBtn: {
    fontFamily: 'Jost, sans-serif', fontSize: '0.72rem',
    letterSpacing: '0.2em', textTransform: 'uppercase',
    color: '#3a3a3a', background: 'none', border: '1px solid #1e1e1e',
    padding: '15px 32px', cursor: 'pointer',
    width: isMobile ? '100%' : 'auto',
  },
  backBtn: {
    fontFamily: 'Jost, sans-serif', fontSize: '0.72rem',
    letterSpacing: '0.2em', textTransform: 'uppercase',
    color: '#3a3a3a', background: 'none', border: 'none',
    padding: '15px 16px', cursor: 'pointer',
    width: isMobile ? '100%' : 'auto',
  },
});

// ── Mood options (New users only) ─────────────────────────────────────────────

const MOOD_OPTIONS = [
  {
    key: 'fresh',
    icon: 'I',
    title: 'Fresh & Clean',
    desc: 'Citrus, aquatic, green. Light and energising.',
    accords: ['Citrus', 'Aquatic', 'Fresh'],
  },
  {
    key: 'warm',
    icon: 'II',
    title: 'Warm & Woody',
    desc: 'Sandalwood, cedar, vanilla. Grounded and inviting.',
    accords: ['Woody', 'Amber', 'Gourmand'],
  },
  {
    key: 'dark',
    icon: 'III',
    title: 'Dark & Intense',
    desc: 'Oud, leather, smoke. Rich and commanding.',
    accords: ['Leather', 'Earthy', 'Oriental'],
  },
  {
    key: 'floral',
    icon: 'IV',
    title: 'Floral & Soft',
    desc: 'Rose, jasmine, iris. Romantic and elegant.',
    accords: ['Floral', 'Powdery', 'Fruity'],
  },
  {
    key: 'unsure',
    icon: '○',
    title: "I'm not sure yet",
    desc: "That's fine — you can set this any time in your profile.",
    accords: [],
  },
];

// ── Tour cards by experience ──────────────────────────────────────────────────

const TOUR_CONTENT = {
  new: {
    title: <>Your fragrance<br /><em style={{ color: '#c9a96e' }}>journey starts here</em></>,
    subtitle: "Flakon grows with you — from your very first bottle to a serious collection.",
    cards: [
      { icon: 'I',   title: 'Discover',   desc: 'Browse hundreds of fragrances. Save anything that interests you to your wishlist.' },
      { icon: 'II',  title: 'Library',    desc: 'Once you own a bottle, add it here. Flakon will recommend it for your daily wear.' },
      { icon: 'III', title: 'Journal',    desc: 'Log what you wear each day and track how each fragrance performs.' },
    ],
    hint: <>Start by heading to <strong style={{ color: '#c9a96e' }}>Discover</strong> — browse and save your first bottle to your wishlist.</>,
    cta: 'Start Exploring →',
  },
  beginner: {
    title: "Here's how Flakon works",
    subtitle: "Three simple tools to build and enjoy your collection.",
    cards: [
      { icon: 'I',   title: 'Library',    desc: 'Add the bottles you own and track how much is left in each one.' },
      { icon: 'II',  title: 'Discover',   desc: 'Browse hundreds of fragrances and find ones that match your taste.' },
      { icon: 'III', title: 'Journal',    desc: 'Log what you wear each day and track how well it performs.' },
    ],
    hint: <>Start by adding your bottles using the <strong style={{ color: '#c9a96e' }}>Add Fragrance</strong> button in your library.</>,
    cta: 'Enter Flakon →',
  },
  intermediate: {
    title: "Here's what Flakon can do",
    subtitle: null,
    cards: [
      { icon: 'I',   title: 'Library',    desc: 'Add your bottles, track levels, and view full fragrance profiles.' },
      { icon: 'II',  title: 'Discover',   desc: 'Browse 200+ fragrances and filter by accord, season and occasion.' },
      { icon: 'III', title: 'Journal',    desc: 'Log every wear, rate longevity and sillage, build your history.' },
    ],
    hint: "Add your collection, then your daily recommendations will start taking shape.",
    cta: 'Enter Flakon →',
  },
  expert: {
    title: "Welcome to Flakon",
    subtitle: "Your profile is set. Here's what's waiting for you.",
    cards: [
      { icon: 'I',   title: 'Library',    desc: 'Log your collection, track bottle levels, view notes and accords.' },
      { icon: 'II',  title: 'Discover',   desc: '200+ fragrances with full profiles. Filter, save to wishlist, explore.' },
      { icon: 'III', title: 'Journal',    desc: 'Log wears, rate longevity and sillage, track your history.' },
    ],
    hint: null,
    cta: 'Enter Flakon →',
  },
};

// ── Main ──────────────────────────────────────────────────────────────────────

const Onboarding = ({ user, token, onComplete }) => {
  const isMobile = useIsMobile();
  const s = S(isMobile);
  const API = process.env.REACT_APP_API_URL;

  const [step, setStep]      = useState(0);
  const [experience, setExp] = useState('');
  const [mood, setMood]      = useState('');
  const [saving, setSaving]  = useState(false);

  // New users get an extra mood screen, so total steps = 4; everyone else = 3
  const totalSteps = experience === 'new' ? 4 : 3;
  const progress   = (step / (totalSteps - 1)) * 100;

  const handleFinish = async () => {
    setSaving(true);
    const selectedMood = MOOD_OPTIONS.find(m => m.key === mood);
    const accords = selectedMood?.accords?.length ? selectedMood.accords : undefined;
    try {
      await axios.patch(
        `${API}/api/profile/preferences`,
        {
          experience:           experience || null,
          onboarding_completed: true,
          ...(accords ? { favourite_accords: accords } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onComplete();
    } catch (err) {
      console.error(err);
      onComplete();
    }
    setSaving(false);
  };

  const tour = TOUR_CONTENT[experience] || TOUR_CONTENT.beginner;

  // Step routing:
  // 0 → Welcome
  // 1 → Experience selection
  // 2 → Mood picker (new only) OR Tour (everyone else)
  // 3 → Tour (new only)

  const goForwardFromExperience = () => setStep(2);
  const goForwardFromMood       = () => setStep(3);
  const tourStep                = experience === 'new' ? 3 : 2;

  const screens = {

    // ── 0: Welcome ────────────────────────────────────────────────────────────
    0: (
      <div key="welcome" style={s.card}>
        <p style={s.eyebrow}>Welcome to Flakon</p>
        <h1 style={s.title}>Your personal<br /><em style={{ color: '#c9a96e' }}>fragrance companion</em></h1>
        <p style={s.subtitle}>
          Recommendations tailored to the weather, your collection, and how you live. Two quick questions and you're in.
        </p>
        <div style={s.btnRow}>
          <button style={s.primaryBtn} onClick={() => setStep(1)}>Get Started</button>
          <button style={s.skipBtn} onClick={handleFinish}>Skip for now</button>
        </div>
      </div>
    ),

    // ── 1: Experience level ───────────────────────────────────────────────────
    1: (
      <div key="experience" style={s.card}>
        <p style={s.eyebrow}>Your Experience</p>
        <h2 style={s.title}>How well do you know fragrance?</h2>
        <p style={s.subtitle}>Be honest — there's no wrong answer.</p>
        <div style={s.expGrid}>
          {[
            { key: 'new',          num: '○',   title: 'New',          desc: "I don't own any fragrances yet — I'm just starting to explore." },
            { key: 'beginner',     num: 'I',   title: 'Beginner',     desc: "I own 1–3 bottles and I'm curious to learn more." },
            { key: 'intermediate', num: 'II',  title: 'Intermediate', desc: "I own 4–10 bottles and I know roughly what I like." },
            { key: 'expert',       num: 'III', title: 'Expert',       desc: "I own 10+ bottles. I know my houses, notes and accords." },
          ].map(({ key, num, title, desc }) => (
            <div key={key} style={s.expCard(experience === key)} onClick={() => setExp(key)}>
              <p style={s.expIcon}>{num}</p>
              <p style={s.expTitle(experience === key)}>{title}</p>
              <p style={s.expDesc}>{desc}</p>
            </div>
          ))}
        </div>
        <div style={s.btnRow}>
          <button
            style={{ ...s.primaryBtn, opacity: experience ? 1 : 0.4, cursor: experience ? 'pointer' : 'not-allowed' }}
            onClick={() => experience && goForwardFromExperience()}
          >
            Continue
          </button>
          <button style={s.backBtn} onClick={() => setStep(0)}>← Back</button>
        </div>
      </div>
    ),

    // ── 2: Mood picker (new users only) ──────────────────────────────────────
    2: experience === 'new' ? (
      <div key="mood" style={s.card}>
        <p style={s.eyebrow}>Your Taste</p>
        <h2 style={s.title}>What draws you<br /><em style={{ color: '#c9a96e' }}>to fragrance?</em></h2>
        <p style={s.subtitle}>Pick the mood that feels most like you. You can always change this later.</p>
        <div style={s.moodGrid}>
          {MOOD_OPTIONS.map(({ key, icon, title, desc }) => (
            <div key={key} style={s.moodCard(mood === key)} onClick={() => setMood(key)}>
              <p style={s.moodIcon}>{icon}</p>
              <p style={s.moodTitle(mood === key)}>{title}</p>
              <p style={s.moodDesc}>{desc}</p>
            </div>
          ))}
        </div>
        <div style={s.btnRow}>
          <button
            style={{ ...s.primaryBtn, opacity: mood ? 1 : 0.4, cursor: mood ? 'pointer' : 'not-allowed' }}
            onClick={() => mood && goForwardFromMood()}
          >
            Continue
          </button>
          <button style={s.backBtn} onClick={() => setStep(1)}>← Back</button>
        </div>
      </div>
    ) : (
      // non-new users: step 2 IS the tour — fall through to tourStep render below
      <div key="tour-placeholder" />
    ),

    // ── 3 (new) / 2 (others): Tour ───────────────────────────────────────────
    [tourStep]: (
      <div key="tour" style={s.card}>
        <p style={s.eyebrow}>You're in</p>
        <h2 style={s.title}>{tour.title}</h2>
        {tour.subtitle && <p style={s.subtitle}>{tour.subtitle}</p>}
        <div style={s.tourGrid}>
          {tour.cards.map((c, i) => (
            <div key={i} style={s.tourCard}>
              <p style={s.tourIcon}>{c.icon}</p>
              <p style={s.tourTitle}>{c.title}</p>
              <p style={s.tourDesc}>{c.desc}</p>
            </div>
          ))}
        </div>
        {tour.hint && <p style={s.hintText}>{tour.hint}</p>}
        <div style={s.btnRow}>
          <button style={s.primaryBtn} onClick={handleFinish} disabled={saving}>
            {saving ? 'Saving...' : tour.cta}
          </button>
          <button style={s.backBtn} onClick={() => setStep(experience === 'new' ? 2 : 1)}>← Back</button>
        </div>
      </div>
    ),
  };

  return (
    <div style={s.overlay}>
      <div style={s.progressBar(progress)} />
      <div style={s.logo}>Flakon</div>
      <div style={s.stepCount}>{step + 1} / {totalSteps}</div>
      {screens[step]}
    </div>
  );
};

export default Onboarding;